import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { Lead, LEAD_TABLE_STATUS, LeadGenProgress, EnrichmentProgress } from '../../types';
import { Spinner } from '../ui';
import { EmptyLeadState } from './EmptyLeadState';
import { useUserActionLogger } from '../../hooks/useUserActionLogger';
import { RowLoadingSkeleton } from '../loading';
import { supabase } from '../../lib/supabase';
import { CompanyReasoningModal } from './CompanyReasoningModal';
import { MatchDetailsPopover } from './MatchDetailsPopover';
import { CellContentPopover } from './CellContentPopover';
import { EnrichmentDetailsPopover } from './EnrichmentDetailsPopover';
import OutOfCreditsModal from '../settings/credits/OutOfCreditsModal';
import { getAppUrl } from '../../utils/validation';
import {
  isURL,
  isEmail,
  getPopupPosition,
  isBaseColumn,
  getCellValue,
  isCellLoading,
  getDisplayUrl,
  hashCriterionTextSync,
  LeadPromptSegment
} from '../../utils/tableUtils';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useToast } from '../../hooks/useToast';
import { useAppData } from '../../contexts/AppDataContext';
import { PLAN_NAME_FREE, FREE_TIER_LEAD_LIMIT, MAX_LEADS_PER_REQUEST } from '../../constants/subscription';
import { Condition } from '../../hooks/useMultiFilter';
import { applyFilters } from '../../utils/applyFilters';
import { apiClient } from '../../lib/api';
import { colors } from '../../styles/tokens';
import { LeadGenProgressBar } from '../ui/LeadGenProgressBar';
import { handleTableAction } from '../../utils/chatActionHandler';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { MobileDatasetView } from './mobile';

// Import extracted table modules
import {
  COLUMN_WIDTHS,
  LEAD_TYPE_BASE_COLUMNS,
  TABLE_TIMINGS,
} from '../../constants/table';
import {
  HoverPreviewTooltip,
  TableToolbar,
  TableHeaderCell,
  TableCell,
  RowNumberCell,
  SelectAllHeaderCell,
  AddColumnButton,
  SelectionMarquee,
  ColumnEnrichmentMenu,
  EmailContextMenu,
  BulkEmailContextMenu,
  AddColumnModal,
  DeleteColumnModal,
} from '../table';
import {
  useCellSelection,
  useHoverPreview,
  useTableSubscriptions,
  useKeyboardNavigation,
  useTableProgress,
  makeCellKey,
  parseCellKey,
} from '../../hooks/table';

interface ProfessionalTableProps {
  leads: Lead[];
  tableType?: 'companies' | 'people' | 'custom';
  onAddColumn?: (columnName: string, columnType: string) => void;
  onRowClick?: (leadId: string) => void;
  tableId: string;
  tableStatus?: string;
  tableData?: any; // Full table object with provider_metadata
  tableName?: string; // Dataset name to show in toolbar
  errorMessage?: string;
  isLoadingInitialData?: boolean;
  onDataRefresh?: () => void;
  isPublicView?: boolean;
  prospectsAnalyzed?: number | null;
  initialSortRules?: Array<{ fieldId: string; direction: 'asc' | 'desc' }>;
  initialFullMatchesFirst?: boolean;
  initialFilterConditions?: Condition[];
  isChatOpen?: boolean; // Controls segment column width expansion
  onStateChange?: (state: {
    sortRules: Array<{ fieldId: string; direction: 'asc' | 'desc' }>;
    fullMatchesFirst: boolean;
    filterConditions: Condition[];
  }) => void;
  onUpdatedDataset?: (tableId: string) => void;
  handleCreateDataset?: (searchQuery: string | null, criteria: string[] | null, tableType: 'companies' | 'people' | 'custom', customType: string, rowCount: number, uploadId: string | null, title: string | null) => void;
  pendingAction?: { type: string; payload?: any } | null;
  onBack?: () => void;
  // Mobile action handlers
  onShare?: () => void;
  onExportCSV?: () => void;
  onExportExcel?: () => void;
  onDelete?: () => void;
  hideHeader?: boolean;
  // Row selection mode - for campaign/email flows
  rowSelectionMode?: boolean; // When true, enables row multi-select with checkboxes
  selectedRowIds?: Set<string>; // Externally controlled selected row IDs
  onRowSelectionChange?: (selectedIds: Set<string>) => void; // Callback when selection changes
  // Visual selection feedback - for showing selection from campaign flow
  visualSelectionMode?: 'all' | 'firstN' | null; // Visual highlight mode
  visualSelectionCount?: number; // Number of rows for 'firstN' mode
  // Progress update callback - for exposing lead generation progress to parent
  onProgressUpdate?: (data: LeadGenProgress | null) => void;
  // Chat workflow callbacks - trigger flows through chat panel
  onStartEnrichmentFlow?: () => void; // Trigger add column / enrichment through chat
  onStartCampaignFlow?: (options?: { email?: string; leadData?: Lead; selectedRowIds?: Set<string> }) => void; // Trigger email campaign through chat
}

interface Column {
  key: string;
  name: string;
  width: number;
  isSegment?: boolean; // Flag to identify segment columns
  isSystemColumn?: boolean; // Flag for system-generated columns (not user data)
  segmentOrder?: number; // Order/index of segment for color mapping (0-based)
  typeLabel?: string;
  description?: string;
}

export const ProfessionalTable = forwardRef<any, ProfessionalTableProps>(({
  leads = [],
  tableType = 'companies',
  onAddColumn,
  onRowClick,
  tableId,
  tableStatus,
  tableData,
  tableName,
  errorMessage,
  isLoadingInitialData = false,
  onDataRefresh,
  isPublicView = false,
  prospectsAnalyzed = null,
  initialSortRules,
  initialFullMatchesFirst,
  initialFilterConditions,
  isChatOpen = true,
  onStateChange,
  onUpdatedDataset,
  // handleCreateDataset, onBack available via props but not used in current implementation
  pendingAction,
  onShare,
  onExportCSV,
  onExportExcel,
  onDelete,
  rowSelectionMode = false,
  selectedRowIds,
  onRowSelectionChange,
  visualSelectionMode = null,
  visualSelectionCount = 0,
  onProgressUpdate,
  onStartEnrichmentFlow,
  onStartCampaignFlow,
}, ref) => {
  const { logClick } = useUserActionLogger();
  const isMobile = useIsMobile();
  const [columns, setColumns] = useState<Column[]>([]);
  const [segments, setSegments] = useState<LeadPromptSegment[]>([]);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const tableRef = useRef<HTMLDivElement>(null);

  // Row selection state (for campaign/email flows)
  const [localSelectedRowIds, setLocalSelectedRowIds] = useState<Set<string>>(new Set());
  const [rowSelectionAnchor, setRowSelectionAnchor] = useState<string | null>(null); // For shift+click range selection
  const [isRowDragging, setIsRowDragging] = useState(false);

  // Use external or local row selection
  const effectiveSelectedRowIds = selectedRowIds || localSelectedRowIds;
  const setEffectiveSelectedRowIds = useCallback((ids: Set<string>) => {
    if (onRowSelectionChange) {
      onRowSelectionChange(ids);
    } else {
      setLocalSelectedRowIds(ids);
    }
  }, [onRowSelectionChange]);

  // Enrichment states
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichingCells, setEnrichingCells] = useState<Set<string>>(new Set()); // Track "columnName:leadId" pairs

  // Column drag-and-drop states
  const [draggingColumnIndex, setDraggingColumnIndex] = useState<number | null>(null);
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Handle search blur - collapse if empty
  const handleSearchBlur = useCallback(() => {
    if (!searchQuery.trim()) {
      setIsSearchExpanded(false);
    }
  }, [searchQuery]);

  // Find More Results state (used by chat actions via pendingAction)
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [isFindingMore, setIsFindingMore] = useState(false);

  // Sort and filter state - initialized from props, managed locally
  const [sortRules, setSortRules] = useState<Array<{ fieldId: string; direction: 'asc' | 'desc' }>>(initialSortRules || []);
  const [fullMatchesFirst, setFullMatchesFirst] = useState(initialFullMatchesFirst || false);
  const [filterConditions, setFilterConditions] = useState<Condition[]>(initialFilterConditions || []);

  // Progress tracking (extracted hook) - provides interimLeads, progressData, fetchProgressData
  const {
    progressData,
    interimLeads,
    fetchProgressData,
  } = useTableProgress({
    tableId,
    tableStatus,
    leads,
    onProgressUpdate,
  });

  // Filter and sort leads (computed early for use by cell selection hook)
  const filteredLeads = useMemo(() => {
    // Combine database leads with interim results
    let combinedLeads = [...leads];

    // Add interim results if available and table is still generating
    if (tableStatus === LEAD_TABLE_STATUS.GENERATING && interimLeads.length > 0) {
      const formattedInterimLeads = interimLeads.slice(leads.length).map((interim, index) => ({
        id: `interim_${index}`,
        data: interim.data,
        reasoning: interim.reasoning || {},
        metadata: interim.metadata || {},
        is_interim: true
      }));
      // @ts-expect-error: Interim leads have different structure than regular leads
      combinedLeads = [...combinedLeads, ...formattedInterimLeads];
    }

    let result = combinedLeads;

    // Apply filter conditions
    if (filterConditions.length > 0) {
      const leadMap = new Map(leads.map(lead => [lead.id, lead]));
      result = applyFilters(
        leads.map(lead => ({ ...lead.data, _id: lead.id })),
        filterConditions
      )
        .map(filtered => leadMap.get(filtered._id)!)
        .filter(Boolean);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((lead) => {
        return Object.values(lead.data).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    }

    // Apply sorting with fullMatchesFirst combined
    if (sortRules.length > 0 || (fullMatchesFirst && searchQuery.trim())) {
      const query = searchQuery.toLowerCase().trim();
      result = [...result].sort((a, b) => {
        if (fullMatchesFirst && query) {
          const aHasExactMatch = Object.values(a.data).some(value =>
            value !== null && value !== undefined && String(value).toLowerCase() === query
          );
          const bHasExactMatch = Object.values(b.data).some(value =>
            value !== null && value !== undefined && String(value).toLowerCase() === query
          );
          if (aHasExactMatch && !bHasExactMatch) return -1;
          if (!aHasExactMatch && bHasExactMatch) return 1;
        }
        for (const rule of sortRules) {
          const aValue = a.data[rule.fieldId];
          const bValue = b.data[rule.fieldId];
          if (aValue === null || aValue === undefined) {
            if (bValue === null || bValue === undefined) continue;
            return rule.direction === 'asc' ? 1 : -1;
          }
          if (bValue === null || bValue === undefined) {
            return rule.direction === 'asc' ? -1 : 1;
          }
          const aStr = String(aValue).toLowerCase();
          const bStr = String(bValue).toLowerCase();
          if (aStr < bStr) return rule.direction === 'asc' ? -1 : 1;
          if (aStr > bStr) return rule.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [leads, interimLeads, tableStatus, filterConditions, searchQuery, sortRules, fullMatchesFirst]);

  // Cell selection (extracted hook)
  const {
    anchor,
    selection,
    isDragging,
    setAnchor,
    setSelection,
    selectCell,
    selectRange,
    clearSelection,
    selectAll,
    isCellSelected,
    isAnchorCell,
    computeSelectionRectangles,
    handleCellMouseDown: hookCellMouseDown,
    handleCellMouseEnter: hookCellMouseEnter,
    handleCellMouseUp: hookCellMouseUp,
  } = useCellSelection({
    leads: filteredLeads,
    columns,
    onRowClick,
  });

  // Track container width for dynamic segment column sizing
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // ResizeObserver to track container width changes
  useEffect(() => {
    if (!tableRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(tableRef.current);
    return () => observer.disconnect();
  }, []);

  // Adjust segment column widths based on available space when chat is closed
  useEffect(() => {
    if (containerWidth === 0 || columns.length === 0) return;

    const segmentColumns = columns.filter(col => col.isSegment);
    const nonSegmentColumns = columns.filter(col => !col.isSegment);

    if (segmentColumns.length === 0) return;

    // Calculate fixed widths: row number + spacer
    const fixedWidth = COLUMN_WIDTHS.ROW_NUMBER + COLUMN_WIDTHS.ADD_COLUMN_SPACER;
    const nonSegmentWidth = nonSegmentColumns.reduce((sum, col) => sum + col.width, 0);
    const availableForSegments = containerWidth - fixedWidth - nonSegmentWidth;

    // Calculate width per segment column
    const widthPerSegment = Math.floor(availableForSegments / segmentColumns.length);

    // Only expand if we have extra space (chat closed) and each column can be wider than minimum
    // When chat is open, use minimum width
    const targetWidth = isChatOpen
      ? COLUMN_WIDTHS.MIN_SEGMENT
      : Math.max(COLUMN_WIDTHS.MIN_SEGMENT, Math.min(widthPerSegment, COLUMN_WIDTHS.MAX_SEGMENT));

    // Check if we need to update
    const needsUpdate = segmentColumns.some(col => col.width !== targetWidth);

    if (needsUpdate) {
      setColumns(prev => prev.map(col =>
        col.isSegment ? { ...col, width: targetWidth } : col
      ));
    }
  }, [containerWidth, isChatOpen, columns.length]);

  // Handle column header click for sorting (base columns only)
  const handleColumnSort = useCallback((columnName: string) => {
    setSortRules(prevRules => {
      const existingRule = prevRules.find(r => r.fieldId === columnName);
      if (!existingRule) {
        // No sort -> ascending
        return [{ fieldId: columnName, direction: 'asc' as const }];
      } else if (existingRule.direction === 'asc') {
        // Ascending -> descending
        return [{ fieldId: columnName, direction: 'desc' as const }];
      } else {
        // Descending -> no sort
        return [];
      }
    });
    setSelection(new Set<string>());
  }, []);

  // Get current sort direction for a column
  const getColumnSortDirection = useCallback((columnName: string): 'asc' | 'desc' | null => {
    const rule = sortRules.find(r => r.fieldId === columnName);
    return rule ? rule.direction : null;
  }, [sortRules]);

  // Enrichment progress (separate from lead gen progress)
  const [enrichmentProgressData, setEnrichmentProgressData] = useState<EnrichmentProgress | null>(null);
  const enrichmentRandomiserRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track if this is the initial mount to avoid notifying parent on first render
  const isInitialMount = useRef(true);

  // Store the current tableId to detect when it changes
  const currentTableIdRef = useRef(tableId);

  // When tableId changes, update state from the new table's initial props
  useEffect(() => {
    if (currentTableIdRef.current !== tableId) {
      currentTableIdRef.current = tableId;
      isInitialMount.current = true;

      // Load the saved state for the new table
      setSortRules(initialSortRules || []);
      setFullMatchesFirst(initialFullMatchesFirst || false);
      setFilterConditions(initialFilterConditions || []);
    }
  }, [tableId, initialSortRules, initialFullMatchesFirst, initialFilterConditions]);

  // Notify parent of state changes so it can persist across table switches
  useEffect(() => {
    // Skip notification on initial mount - only notify on actual user changes
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onStateChange) {
      onStateChange({ sortRules, fullMatchesFirst, filterConditions });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortRules, fullMatchesFirst, filterConditions]);

  useEffect(() => {
    const colName = enrichmentProgressData?.column;
    if (!colName) return;
    const clearProgressInterval = () => {
      if (enrichmentRandomiserRef.current) {
        clearInterval(enrichmentRandomiserRef.current);
        enrichmentRandomiserRef.current = null;
      }
    };

    clearProgressInterval();

    let allEnrichmentsCompleted = true;

    leads.forEach((lead) => {
      if (!lead.cellStates?.[colName] || isCellLoading(lead, colName)) {
        allEnrichmentsCompleted = false
      }
    })

    if (allEnrichmentsCompleted && enrichingCells.size === 0) {
      console.debug("All enrichments completed");
      setEnrichmentProgressData(null);
    } else {
      enrichmentRandomiserRef.current = setInterval(() => {
        setEnrichmentProgressData(prev => 
          prev ? { ...prev, progress: Math.min(prev.progress + (Math.random() * 5), 98) } : prev
        );
      }, 1000);
    }

    return clearProgressInterval;
  }, [leads, columns, enrichingCells, enrichmentProgressData?.column]);

  const [columnEnrichmentPopup, setColumnEnrichmentPopup] = useState<{
    show: boolean;
    columnName: string;
    columnKey: string;
    x: number;
    y: number;
  }>({ show: false, columnName: '', columnKey: '', x: 0, y: 0 });
  

  // Delete column modal state
  const [deleteColumnModal, setDeleteColumnModal] = useState<{
    show: boolean;
    columnName: string;
    columnKey: string;
  }>({ show: false, columnName: '', columnKey: '' });

  // Email context menu state
  const [emailContextMenu, setEmailContextMenu] = useState<{
    show: boolean;
    email: string;
    leadData: Lead;
    x: number;
    y: number;
  }>({ show: false, email: '', leadData: {} as Lead, x: 0, y: 0 });

  // Bulk email context menu state
  const [bulkEmailContextMenu, setBulkEmailContextMenu] = useState<{
    show: boolean;
    x: number;
    y: number;
    selectedColumn: string;
    selectedRows: Set<string>;
  }>({ show: false, x: 0, y: 0, selectedRows: new Set(), selectedColumn: '' });

  // Column editing state
  const [editingColumn, setEditingColumn] = useState<{
    key: string;
    name: string;
  } | null>(null);
  const [editColumnName, setEditColumnName] = useState('');

  // Centralized error handling and toast notifications
  const { showOutOfCreditsModal, handleError, closeOutOfCreditsModal } = useErrorHandler();
  const { showToast } = useToast();
  const { subscriptionData } = useAppData();
  const isFreeTier = subscriptionData?.plan_name === PLAN_NAME_FREE;

  // Hover preview (extracted hook)
  const {
    hoverPreview,
    hoverPreviewRef,
    isMouseInPreview,
    handleHeaderHoverPreview,
    handleHeaderHoverPreviewLeave,
    closeHoverPreview,
  } = useHoverPreview({ columns });

  // Real-time subscriptions (extracted hook)
  useTableSubscriptions({
    tableId,
    onCellUpdate: useCallback((columnName: string, rowId: string) => {
      const cellKey = `${columnName}:${rowId}`;
      setEnrichingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }, []),
    onDataRefresh,
  });

  // Company Reasoning Modal state
  const [companyReasoningModal, setCompanyReasoningModal] = useState<{
    isOpen: boolean;
    companyName: string;
    companyUrl?: string;
    summary?: string;
    reasoning: Record<string, any>;
    overallStatus: 'match' | 'no-match' | 'partial';
    criteria?: string;
  }>({
    isOpen: false,
    companyName: '',
    reasoning: {},
    overallStatus: 'match',
    criteria: undefined
  });

  // Match Details Popover state (cell-anchored)
  const [matchPopover, setMatchPopover] = useState<{
    isOpen: boolean;
    anchorRect: DOMRect | null;
    companyName: string;
    companyUrl?: string;
    criterion: any | null;
    criterionIndex: number;
    overallStatus: 'match' | 'no-match' | 'partial';
    reasoning: Record<string, any>;
  }>({
    isOpen: false,
    anchorRect: null,
    companyName: '',
    criterion: null,
    criterionIndex: 1,
    overallStatus: 'match',
    reasoning: {}
  });

  // Cell Content Popover state (for descriptions, names, URLs)
  const [cellContentPopover, setCellContentPopover] = useState<{
    isOpen: boolean;
    anchorRect: DOMRect | null;
    content: string;
    columnName: string;
    isEmail: boolean;
    isUrl: boolean;
    companyName: string;
    leadData: Lead | null;
  }>({
    isOpen: false,
    anchorRect: null,
    content: '',
    columnName: '',
    isEmail: false,
    isUrl: false,
    companyName: '',
    leadData: null
  });

  // Enrichment Details Popover state (cell-anchored)
  const [enrichmentPopover, setEnrichmentPopover] = useState<{
    isOpen: boolean;
    anchorRect: DOMRect | null;
    columnName: string;
    rowName: string;
    cellValue: string;
    enrichmentSummary: string;
    dataSources: string[];
    isLoading?: boolean;
    leadData?: Lead | null;
  }>({
    isOpen: false,
    anchorRect: null,
    columnName: '',
    rowName: '',
    cellValue: '',
    enrichmentSummary: '',
    dataSources: [],
    isLoading: false,
    leadData: null
  });

  // Cache enrichment metadata for faster modal opening
  const enrichmentCache = useRef<Map<string, Record<string, any>>>(new Map());

  // Defensive check for async operations and subscriptions
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Pre-fetch enrichment metadata when selected row changes (populates cache)
  useEffect(() => {
    if (anchor?.rowId && !enrichmentCache.current.has(anchor.rowId)) {
      const currentRowId = anchor.rowId;
      apiClient.getEnrichmentMetadata(tableId, [currentRowId])
        .then((metadata) => {
          if (isMountedRef.current && anchor?.rowId === currentRowId) {
            enrichmentCache.current.set(currentRowId, metadata[currentRowId] || {});
          }
        })
        .catch((error) => {
          console.error('Failed to pre-fetch enrichment metadata:', error);
        });
    }
  }, [anchor?.rowId, tableId]);

  // Expose columns to parent component
  useImperativeHandle(ref, () => ({
    getColumns: () => columns
  }), [columns]);

  // Keyboard navigation helpers - handles popover updates during navigation
  const moveActiveCell = useCallback((direction: 'up' | 'down' | 'left' | 'right', extendSelection: boolean = false) => {
    if (!anchor) return;

    const currentRowIdx = filteredLeads.findIndex(l => l.id === anchor.rowId);
    const currentColIdx = columns.findIndex(c => c.key === anchor.colId);

    if (currentRowIdx === -1 || currentColIdx === -1) return;

    let newRowIdx = currentRowIdx;
    let newColIdx = currentColIdx;

    switch (direction) {
      case 'up':
        newRowIdx = Math.max(0, currentRowIdx - 1);
        break;
      case 'down':
        newRowIdx = Math.min(filteredLeads.length - 1, currentRowIdx + 1);
        break;
      case 'left':
        newColIdx = Math.max(0, currentColIdx - 1);
        break;
      case 'right':
        newColIdx = Math.min(columns.length - 1, currentColIdx + 1);
        break;
    }

    const newLead = filteredLeads[newRowIdx];
    const newCol = columns[newColIdx];

    if (!newLead.id || !newCol.key) return;

    if (extendSelection) {
      // Shift+Arrow: Extend selection from original anchor
      selectRange(anchor, { rowId: newLead.id, colId: newCol.key });
    } else {
      // Arrow: Move to new cell, collapse selection
      selectCell(newLead.id, newCol.key, false);

      // If popover is open and new cell is a segment column with reasoning, update popover
      if (matchPopover.isOpen && newCol.isSegment && newLead.reasoning && Object.keys(newLead.reasoning).length > 0) {
        const companyName = newLead.name || newLead.data?.name || newLead.data?.Name || 'Company';
        const companyUrl = newLead.data?.url || newLead.data?.URL || newLead.data?.website;
        const cellValue = getCellValue(newLead, newCol.name, newCol.name);

        let status: 'match' | 'no-match' | 'partial' = 'match';
        if (cellValue === 'Miss') status = 'no-match';
        else if (cellValue === 'Unclear') status = 'partial';

        // Find the specific criterion for this column
        const reasoningEntries = Object.entries(newLead.reasoning);
        const criterionEntry = reasoningEntries.find(([, r]) =>
          r.criterion_text === newCol.name ||
          newCol.name.toLowerCase().includes(r.criterion_text?.toLowerCase()?.substring(0, 20))
        );
        const criterionIndex = criterionEntry
          ? reasoningEntries.findIndex(([k]) => k === criterionEntry[0]) + 1
          : 1;

        // Get the cell element for anchor positioning
        const cellElement = document.querySelector(`[data-row-id="${newLead.id}"][data-col-id="${newCol.key}"]`) as HTMLElement;
        const anchorRect = cellElement?.getBoundingClientRect() || matchPopover.anchorRect;

        setMatchPopover({
          isOpen: true,
          anchorRect,
          companyName,
          companyUrl,
          criterion: criterionEntry ? criterionEntry[1] : reasoningEntries[0]?.[1] || null,
          criterionIndex,
          overallStatus: status,
          reasoning: newLead.reasoning
        });
      } else if (matchPopover.isOpen && !newCol.isSegment) {
        // Close popover if navigated away from segment column
        setMatchPopover({
          isOpen: false,
          anchorRect: null,
          companyName: '',
          criterion: null,
          criterionIndex: 1,
          overallStatus: 'match',
          reasoning: {}
        });
      }

      // Handle CellContentPopover navigation for base columns
      const isBase = isBaseColumn(newCol.name, tableType || 'companies');
      const cellValue = getCellValue(newLead, newCol.name, newCol.name);

      if (cellContentPopover.isOpen && isBase && cellValue && cellValue.trim()) {
        const companyName = newLead.name || newLead.data?.name || newLead.data?.Name || '';
        const cellElement = document.querySelector(`[data-row-id="${newLead.id}"][data-col-id="${newCol.key}"]`) as HTMLElement;
        const anchorRect = cellElement?.getBoundingClientRect() || cellContentPopover.anchorRect;

        // Check if it's a URL or email
        const colNameLower = newCol.name.toLowerCase();
        const isUrl = colNameLower.includes('url') || colNameLower.includes('website') || colNameLower.includes('link') ||
          (typeof cellValue === 'string' && (cellValue.startsWith('http') || cellValue.includes('.com') || cellValue.includes('.org')));
        const cellIsEmail = isEmail(cellValue);

        setCellContentPopover({
          isOpen: true,
          anchorRect,
          content: cellValue,
          columnName: newCol.name,
          isEmail: cellIsEmail,
          isUrl: isUrl,
          companyName,
          leadData: newLead
        });
      } else if (cellContentPopover.isOpen && !isBase) {
        // Close popover if navigated away from base column
        setCellContentPopover({
          isOpen: false,
          anchorRect: null,
          content: '',
          columnName: '',
          isEmail: false,
          isUrl: false,
          companyName: '',
          leadData: null
        });
      }
    }
  }, [anchor, filteredLeads, columns, selectRange, selectCell, matchPopover.isOpen, matchPopover.anchorRect, cellContentPopover.isOpen, cellContentPopover.anchorRect, tableType]);

  // Keyboard navigation (extracted hook)
  useKeyboardNavigation({
    anchor,
    leads: filteredLeads,
    columns,
    isDragging,
    onMove: moveActiveCell,
    onSelectAll: selectAll,
    onClearSelection: clearSelection,
    onSelectCell: selectCell,
    onSelectRange: selectRange,
  });

  // Handle column rename
  const handleColumnRename = async (columnKey: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || !editingColumn || trimmed === editingColumn.name) {
      setEditingColumn(null);
      setEditColumnName('');
      return;
    }

    try {
      await apiClient.renameDatasetColumn(tableId, editingColumn.name, trimmed);
      onUpdatedDataset?.(tableId);

      // Update local state
      setColumns(prevColumns =>
        prevColumns.map(col =>
          col.key === columnKey ? { ...col, name: trimmed } : col
        )
      );

      // Refresh table data to reflect the column name change
      if (onDataRefresh) {
        onDataRefresh();
      }
    } catch (error) {
      console.error('Error renaming column:', error);
      showToast('Failed to rename column. Please try again.', 'error');
    } finally {
      setEditingColumn(null);
      setEditColumnName('');
    }
  };

  // Start column editing
  const startColumnEdit = (columnKey: string, columnName: string) => {
    if (isBaseColumn(columnName, tableType)) return; // Don't allow editing base columns
    setEditingColumn({ key: columnKey, name: columnName });
    setEditColumnName(columnName);
  };

  // Open column enrichment menu
  const handleOpenEnrichmentMenu = useCallback((e: React.MouseEvent, columnKey: string, columnName: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const position = getPopupPosition(rect, 288, 120);
    setColumnEnrichmentPopup({
      show: true,
      columnName,
      columnKey,
      x: position.x,
      y: position.y
    });
    logClick(e.currentTarget as HTMLElement, {
      action: 'open_column_enrichment_popup',
      table_type: tableType,
      column_name: columnName,
      column_key: columnKey
    });
  }, [tableType, logClick]);

  // Select all cells in a column
  const handleSelectColumn = useCallback((columnKey: string) => {
    const columnCells = new Set<string>();
    filteredLeads.forEach(lead => {
      if (lead.id) {
        columnCells.add(makeCellKey(lead.id, columnKey));
      }
    });
    setSelection(columnCells);
    if (filteredLeads.length > 0 && filteredLeads[0].id) {
      setAnchor({ rowId: filteredLeads[0].id, colId: columnKey });
    }
  }, [filteredLeads]);

  // Select all handler (for header cell)
  const handleSelectAll = useCallback(() => {
    const allRowIds = filteredLeads.map(l => l.id).filter(Boolean) as string[];
    const allRowsSelected = rowSelectionMode && allRowIds.length > 0 && allRowIds.every(id => effectiveSelectedRowIds.has(id));

    if (rowSelectionMode) {
      // Row selection mode: toggle all rows
      if (allRowsSelected) {
        setEffectiveSelectedRowIds(new Set());
      } else {
        setEffectiveSelectedRowIds(new Set(allRowIds));
      }
    } else {
      // Cell selection mode: select all cells
      const allCells = new Set<string>();
      filteredLeads.forEach(lead => {
        if (lead.id) {
          columns.forEach(col => {
            if (col.key) {
              allCells.add(makeCellKey(lead.id!, col.key));
            }
          });
        }
      });
      setSelection(allCells);
      if (filteredLeads.length > 0 && columns.length > 0 && filteredLeads[0].id) {
        setAnchor({ rowId: filteredLeads[0].id, colId: columns[0].key });
      }
    }
  }, [filteredLeads, columns, rowSelectionMode, effectiveSelectedRowIds, setEffectiveSelectedRowIds, setSelection, setAnchor]);

  // Open Match Details Popover (extracted from row render)
  const openMatchPopover = useCallback((lead: Lead, columnName: string, anchorElement: HTMLElement) => {
    if (!lead.reasoning || Object.keys(lead.reasoning).length === 0) return;

    const companyName = lead.name || lead.data?.name || lead.data?.Name || 'Company';
    const companyUrl = lead.data?.url || lead.data?.URL || lead.data?.website;

    const cellValue = getCellValue(lead, columnName, columnName);
    let status: 'match' | 'no-match' | 'partial' = 'match';
    if (cellValue === 'Miss') status = 'no-match';
    else if (cellValue === 'Unclear') status = 'partial';

    // Find the specific criterion for this column
    const reasoningEntries = Object.entries(lead.reasoning);
    const criterionEntry = reasoningEntries.find(([, r]) =>
      r.criterion_text === columnName ||
      columnName.toLowerCase().includes(r.criterion_text?.toLowerCase()?.substring(0, 20))
    );

    const criterionIndex = criterionEntry
      ? reasoningEntries.findIndex(([k]) => k === criterionEntry[0]) + 1
      : 1;

    const anchorRect = anchorElement.getBoundingClientRect();

    setMatchPopover({
      isOpen: true,
      anchorRect,
      companyName,
      companyUrl,
      criterion: criterionEntry ? criterionEntry[1] : reasoningEntries[0]?.[1] || null,
      criterionIndex,
      overallStatus: status,
      reasoning: lead.reasoning
    });
  }, []);

  // Open Enrichment Details Popover (cell-anchored)
  const openEnrichmentPopover = useCallback((lead: Lead, columnName: string, anchorRect: DOMRect | null) => {
    if (columnName.toLowerCase().includes('confidence')) return;

    const cachedMetadata = enrichmentCache.current.get(lead.id);
    const rowName = lead.name || lead.data?.name || lead.data?.Name || 'Row';
    const cellValue = getCellValue(lead, columnName, columnName);

    if (cachedMetadata && cachedMetadata[columnName]) {
      const enrichment = cachedMetadata[columnName];
      // Show popover with whatever data we have from cache
      setEnrichmentPopover({
        isOpen: true,
        anchorRect,
        columnName: columnName,
        rowName: rowName,
        cellValue: cellValue,
        enrichmentSummary: enrichment.summary || 'No reasoning available for this enrichment.',
        dataSources: enrichment.dataSources || [],
        isLoading: false,
        leadData: lead
      });
    } else {
      // Show loading popover immediately
      setEnrichmentPopover({
        isOpen: true,
        anchorRect,
        columnName: columnName,
        rowName: rowName,
        cellValue: cellValue,
        enrichmentSummary: '',
        dataSources: [],
        isLoading: true,
        leadData: lead
      });

      // Fetch if not cached
      apiClient.getEnrichmentMetadata(tableId, [lead.id])
        .then((metadata) => {
          const rowMetadata = metadata[lead.id];
          if (rowMetadata) {
            enrichmentCache.current.set(lead.id, rowMetadata);
            if (rowMetadata[columnName]) {
              const enrichment = rowMetadata[columnName];
              // Show popover with whatever data we have
              setEnrichmentPopover({
                isOpen: true,
                anchorRect,
                columnName: columnName,
                rowName: rowName,
                cellValue: cellValue,
                enrichmentSummary: enrichment.summary || 'No reasoning available for this enrichment.',
                dataSources: enrichment.dataSources || [],
                isLoading: false,
                leadData: lead
              });
            } else {
              // Column not found in enrichment metadata
              setEnrichmentPopover({
                isOpen: true,
                anchorRect,
                columnName: columnName,
                rowName: rowName,
                cellValue: cellValue,
                enrichmentSummary: 'No enrichment data available for this field.',
                dataSources: [],
                isLoading: false,
                leadData: lead
              });
            }
          } else {
            // Row not found in metadata
            setEnrichmentPopover({
              isOpen: true,
              anchorRect,
              columnName: columnName,
              rowName: rowName,
              cellValue: cellValue,
              enrichmentSummary: 'No enrichment data available.',
              dataSources: [],
              isLoading: false,
              leadData: lead
            });
          }
        })
        .catch((error) => {
          console.error('Failed to fetch enrichment metadata:', error);
          setEnrichmentPopover({
            isOpen: true,
            anchorRect,
            columnName: columnName,
            rowName: rowName,
            cellValue: cellValue,
            enrichmentSummary: 'Failed to load enrichment data. Please try again.',
            dataSources: [],
            isLoading: false,
            leadData: lead
          });
        });
    }
  }, [tableId]);

  // Memoized modal handlers to prevent unnecessary re-renders
  const handleCloseModal = useCallback(() => {
    setShowAddColumn(false);
    setNewColumnName('');
  }, []);

  // Calculate if find more is available
  const canFindMore = tableData?.status === 'completed' || tableData?.status === 'completed_no_results';

  // Find More Results handler (triggered by chat actions via pendingAction)
  const handleFindMoreResults = useCallback(async () => {
    // Allow find more for completed tables (with or without results)
    if (isPublicView || !tableData || !canFindMore) {
      return;
    }

    // Get count from preset (set by chat action)
    const count = selectedPreset;
    if (count === null || count < 1) {
      return;
    }

    // Check free tier limit
    if (isFreeTier && count > FREE_TIER_LEAD_LIMIT) {
      return;
    }

    // Check max limit
    const maxLimit = isFreeTier ? FREE_TIER_LEAD_LIMIT : MAX_LEADS_PER_REQUEST;
    if (count > maxLimit) {
      return;
    }

    setIsFindingMore(true);

    try {
      await apiClient.findMoreResults(tableId, count);
      // Success - keep loading state until subscription updates status to 'completed'
      setSelectedPreset(null); // Reset preset selection
      setTimeout(() => fetchProgressData(), TABLE_TIMINGS.PROGRESS_POLL_INTERVAL);
    } catch (error: any) {
      console.error('Error finding more results:', error);
      const errorMessage = error?.message || 'Failed to find more results';
      showToast(errorMessage, 'error');
      setIsFindingMore(false); // Only stop loading on error
    }
  }, [isPublicView, tableData, canFindMore, selectedPreset, tableId, showToast, fetchProgressData, isFreeTier]);

  // Track previous status to detect transitions
  const prevStatusRef = useRef<string | undefined>();

  // Sync isFindingMore with tableData.status changes
  useEffect(() => {
    const currentStatus = tableData?.status;
    const prevStatus = prevStatusRef.current;

    // Only clear loading state when transitioning FROM 'generating' TO another state
    if (isFindingMore && prevStatus === 'generating' && currentStatus && currentStatus !== 'generating') {
      setIsFindingMore(false);
      showToast('Finished finding more results', 'success')
      // Trigger parent to reload leads data only on successful completion
      if (currentStatus === 'completed' && onDataRefresh) {
        onDataRefresh();
      }
    }

    // Update previous status
    prevStatusRef.current = currentStatus;
  }, [tableData?.status, isFindingMore, onDataRefresh, showToast]);

  // Set default selected cell to first cell when leads change
  useEffect(() => {
    // Only auto-select first cell when leads first load (and no cell is selected yet)
    if (leads.length > 0 && selection.size === 0 && leads[0].id && columns.length > 0) {
      const firstRowId = leads[0].id;
      const firstColId = columns[0].key;
      selectCell(firstRowId, firstColId, false);
    }
  }, [leads, columns, selection.size, selectCell]);

  // Calculate loading state directly in render to prevent flash of empty state
  const isLoadingRows = useMemo(() => {
    // If we have no leads yet, show loading state - but only if table is still processing
    // If table is completed with no results, don't show loading state
    const isProcessing = tableStatus === LEAD_TABLE_STATUS.GENERATING ||
                        isLoadingInitialData ||
                        (!tableStatus && leads.length === 0);
    const isCompleted = tableStatus === LEAD_TABLE_STATUS.COMPLETED ||
                       tableStatus === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS ||
                       tableStatus?.startsWith('error');

    // Show loading state if:
    // 1. No leads yet and table is processing
    // 2. OR we have leads but columns/segments aren't ready yet (prevent "Unknown" flash)
    const columnsNotReady = leads.length > 0 && columns.length === 0;

    return (leads.length === 0 && interimLeads.length === 0 && tableId && (isProcessing || isLoadingInitialData) && !isCompleted) || columnsNotReady;
  }, [leads.length, interimLeads.length, tableId, tableStatus, isLoadingInitialData, columns.length]);

  // Global mouseup listener for drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleCellMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
    // handleCellMouseUp uses stable React setters (setIsDragging, setDragStart), safe to omit from deps

  }, [isDragging]);

  // Global mouseup listener for row drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isRowDragging) {
        setIsRowDragging(false);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isRowDragging]);

  // Extract criteria from lead_row_reasoning data
  // The reasoning object is keyed by criterion_text, so we use that directly as the segment id
  const extractCriteriaFromReasoning = () => {
    if (leads.length === 0) return [];

    const criteriaSet = new Set<string>();

    // Collect all unique criterion_text values from reasoning data
    leads.forEach(lead => {
      if (lead.reasoning) {
        Object.keys(lead.reasoning).forEach(key => {
          const reasoningItem = lead.reasoning?.[key];
          // Use the criterion_text as the unique identifier
          if (reasoningItem.criterion_text) {
            criteriaSet.add(reasoningItem.criterion_text);
          }
        });
      }
    });

    // Create segment objects using hashed criterion_text as id
    return Array.from(criteriaSet).map((criterion, index) => ({
      id: hashCriterionTextSync(criterion),
      segment_text: criterion,
      segment_order: index + 1,
      lead_table_id: tableId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  };

  // Clear columns and segments immediately when tableId changes (before new data loads)
  // This prevents old table headers from showing during loading
  useEffect(() => {
    // Always clear state when tableId changes
    setColumns([]);
    setSegments([]);
    // Loading state is now computed automatically based on leads/status
  }, [tableId]);

  // Unified table initialization: Build columns from leads data synchronously
  useEffect(() => {
    if (!tableId) return;

    // Only initialize when we have leads data (skip initial empty state during loading)
    if (leads.length === 0) {
      // During loading, set skeleton columns based on table type
      // This ensures appropriate loading animation even when switching tables
      const baseColumnNames = LEAD_TYPE_BASE_COLUMNS[tableType] || LEAD_TYPE_BASE_COLUMNS.custom;
      const skeletonColumns = baseColumnNames.map((name) => ({
        key: name.toLowerCase().replace(/\s+/g, '_'),
        name,
        width: name === 'Name' ? 180 : name === 'URL' ? 220 : 150
      }));

      // Always set skeleton columns during loading to ensure correct structure preview
      // This prevents showing wrong columns from previous table
      setColumns(skeletonColumns);
      setSegments([]); // Clear segments during loading
      return;
    }

    // Extract criteria from reasoning data
    const tableSegments = extractCriteriaFromReasoning();

    // Build columns from the first lead's data structure
    const firstLead = leads[0];
    if (!firstLead || !firstLead.data) return;

    // Get all column names from lead data
    // Data is clean - cellStates and metadata are separate, no suffix filtering needed
    const columnNames = Object.keys(firstLead.data);

    // Create a map to handle duplicate column names
    const columnMap = new Map();
    const orderedColumns = columnNames.map((colName, index) => {
      let key = colName.toLowerCase().replace(/\s+/g, '_');

      // Handle duplicate keys by adding index suffix
      if (columnMap.has(key)) {
        key = `${key}_${index}`;
      }
      columnMap.set(key, true);

      return {
        key,
        name: colName,
        width: colName === 'Name' ? 180 : colName.toLowerCase().includes('email') || colName.toLowerCase().includes('url') ? 220 : 150
      };
    });

    // Separate base columns from enrichment columns
    // Build base columns in the exact order specified in LEAD_TYPE_BASE_COLUMNS
    const baseColumnNames = LEAD_TYPE_BASE_COLUMNS[tableType] || LEAD_TYPE_BASE_COLUMNS.custom;
    const baseColumns = baseColumnNames
      .map(baseName => orderedColumns.find(col => col.name === baseName))
      .filter((col): col is { key: string; name: string; width: number } => col !== undefined);

    // Enrichment columns are everything else (not in base columns)
    const enrichmentColumns = orderedColumns.filter(col => !baseColumnNames.some(baseName => col.name === baseName));

    // Create segment columns - fixed width, headers wrap to 2 lines
    const segmentColumns = tableSegments.map((segment, index) => ({
      key: `segment_${segment.id}`, // segment.id IS the criterion_text
      name: segment.segment_text,
      width: 140, // Fixed width - header wraps to 2 lines, hover shows full text
      isSegment: true,
      segmentOrder: index,
      criterionText: segment.id // Store for easier lookup
    }));

    // Check if any leads are from monitors - if so, add "Monitored" column
    const hasMonitorItems = leads.some(lead => lead.sourceType === 'monitor');
    const monitoredColumn: Column[] = hasMonitorItems ? [{
      key: 'monitored_time',
      name: 'Monitored',
      width: 110,
      isSystemColumn: true,
    }] : [];

    // Set both segments and columns together in one go - prevents rebuild
    setSegments(tableSegments);
    setColumns([...baseColumns, ...monitoredColumn, ...segmentColumns, ...enrichmentColumns]);
  }, [tableId, leads, tableType]); // React when leads change

  // Handle column resize
  const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    
    const column = columns.find(col => col.key === columnKey);
    if (column) {
      setResizeStartWidth(column.width);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (resizingColumn) {
        const diff = e.clientX - resizeStartX;
        const newWidth = Math.max(100, resizeStartWidth + diff);
        
        setColumns(prev => prev.map(col => 
          col.key === resizingColumn 
            ? { ...col, width: newWidth }
            : col
        ));
      }
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    if (resizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  // Handle column drag-and-drop for reordering
  const handleColumnDragStart = (e: React.DragEvent, index: number) => {
    setDraggingColumnIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    // Add some visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.4';
    }
  };

  const handleColumnDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggingColumnIndex(null);
    setDragOverColumnIndex(null);
  };

  const handleColumnDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault(); // Allow drop
    e.dataTransfer.dropEffect = 'move';

    if (draggingColumnIndex !== null && draggingColumnIndex !== index) {
      setDragOverColumnIndex(index);
    }
  };

  const handleColumnDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingColumnIndex !== null && draggingColumnIndex !== index) {
      setDragOverColumnIndex(index);
    }
  };

  const handleColumnDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the column
    if (e.currentTarget === e.target) {
      setDragOverColumnIndex(null);
    }
  };

  const handleColumnDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggingColumnIndex === null || draggingColumnIndex === dropIndex) {
      setDragOverColumnIndex(null);
      return;
    }

    // Reorder columns array
    const newColumns = [...columns];
    const [draggedColumn] = newColumns.splice(draggingColumnIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);

    setColumns(newColumns);
    setDraggingColumnIndex(null);
    setDragOverColumnIndex(null);
  };

  const contextBulkEmailClick = () => {
    const selectedLeads = filteredLeads.filter(lead => bulkEmailContextMenu.selectedRows.has(lead.id));
    handleBulkEmailClick(bulkEmailContextMenu.selectedColumn, selectedLeads);
    setBulkEmailContextMenu({
      show: false,
      x: 0,
      y: 0,
      selectedColumn: '',
      selectedRows: new Set()
    });
  }

  const handleBulkEmailClick = useCallback((_columnKey: string, selectedLeads: Lead[] | null) => {
    if (!onStartCampaignFlow) return;

    // Pass the selected row IDs to pre-select in campaign flow
    const selectedRowIds = selectedLeads
      ? new Set(selectedLeads.map(l => l.id).filter((id): id is string => !!id))
      : undefined;
    onStartCampaignFlow({ selectedRowIds });
  }, [onStartCampaignFlow]);

  const handleAddColumn = useCallback(async () => {
    if (newColumnName.trim() && tableId && !isEnriching) { // Prevent double-clicks during enrichment
      try {
        onUpdatedDataset?.(tableId);
        // Set enriching state immediately if enrichment is requested
        setIsEnriching(true);

        // Use centralized atomic column creation to prevent duplicates
        const result = await apiClient.ensureTableColumn(tableId, newColumnName, 'text');

        if (!result.success) {
          throw new Error(result.message || 'Failed to create column');
        }

        // Update local columns state
        const newColumn = {
          key: newColumnName.toLowerCase().replace(/\s+/g, '_'),
          name: newColumnName,
          width: 150
        };

        setColumns(prev => {
          // Check if column already exists in local state
          if (prev.find(col => col.name === newColumnName)) {
            return prev; // Don't add duplicate
          }
          return [...prev, newColumn];
        });

        // Call parent callback
        if (onAddColumn) {
          onAddColumn(newColumn.name, 'text');
        }

        // Close modal immediately to show the enrichment in progress
        setNewColumnName('');
        setShowAddColumn(false);

        // Set loading state on cells and start enrichment process
        // Mark cells as enriching immediately (for loading animation)
        const cellsToEnrich = new Set<string>();
        leads.forEach(lead => {
          if (lead.id) {
            cellsToEnrich.add(`${newColumnName}:${lead.id}`);
          }
        });
        setEnrichingCells(cellsToEnrich);
        setEnrichmentProgressData({
          column: newColumnName,
          total: leads.length,
          progress: 0,
          status: `Enriching ${newColumnName.slice(0, 10)}${newColumnName.length > 10 ? '...' : ''}`
        });

        // Start enrichment (format hint is handled by AddColumnModal when using the UI)
        try {
          await apiClient.enrichTableColumn(tableId, {
            column_name: newColumnName,
            row_limit: 'all'
          });
        } catch (error: unknown) {
          handleError(error as string | Error, 'Column Enrichment');
          throw error;
        }

        // Success toast
        showToast(`Column '${newColumnName}' added. AI enrichment started on ${leads.length} visible rows.`, 'success');
        setIsEnriching(false);

      } catch (error) {
        console.error('Error adding column:', error);
        setIsEnriching(false);
        setEnrichingCells(new Set());
        setEnrichmentProgressData(null);
      }
    }
  }, [handleError, isEnriching, leads, newColumnName, onAddColumn, onUpdatedDataset, showToast, tableId]);

  useEffect(() => {
    if (pendingAction) {
      handleTableAction(pendingAction, {
        handleBulkEmailClick,
        columns,
        filteredLeads,
        handleFindMoreResults: (actionParameters?: any) => {
          if (actionParameters?.count) {
            // Set the count from chat action and trigger
            setSelectedPreset(actionParameters.count);
            setTimeout(() => handleFindMoreResults(), 100);
          } else {
            // Use default count for chat actions
            setSelectedPreset(50);
            setTimeout(() => handleFindMoreResults(), 100);
          }
        },
        handleAddColumn: (columnName?: string) => {
          if (columnName) {
            // Set the column name and trigger enrichment
            setNewColumnName(columnName);
            // Trigger handleAddColumn after state is set
            setTimeout(() => handleAddColumn(), 100);
          } else {
            handleAddColumn();
          }
        }
      });
    }
  }, [pendingAction, handleBulkEmailClick, columns, filteredLeads, handleFindMoreResults, handleAddColumn]);

  // Handle rerun incomplete enrichments - enrich only empty/null cells
  const handleRerunIncompleteEnrichments = async (columnName: string) => {
    try {
      setIsEnriching(true);
      setColumnEnrichmentPopup({ show: false, columnName: '', columnKey: '', x: 0, y: 0 });

      // Find column ID to check for incomplete cells
      const { data: columnData } = await supabase
        .from('lead_columns')
        .select('id')
        .eq('lead_table_id', tableId)
        .eq('name', columnName)
        .single();

      if (!columnData) {
        throw new Error('Column not found');
      }

      const columnId = columnData.id;

      // Get all row IDs for this table
      const { data: allRows } = await supabase
        .from('lead_rows')
        .select('id')
        .eq('lead_table_id', tableId);

      if (!allRows || allRows.length === 0) {
        throw new Error('No rows found');
      }

      // Find existing cells to identify incomplete ones
      const { data: existingCells } = await supabase
        .from('lead_cells')
        .select('id, row_id, value')
        .eq('column_id', columnId)
        .in('row_id', allRows.map(row => row.id));

      // Identify incomplete rows and set up loading states
      const existingCellMap = new Map();
      existingCells?.forEach(cell => {
        existingCellMap.set(cell.row_id, cell);
      });

      const cellsToEnrich = new Set<string>();
      let incompleteCount = 0;
      
      for (const row of allRows) {
        const existingCell = existingCellMap.get(row.id);
        if (!existingCell || !existingCell.value || existingCell.value === 'unknown' || existingCell.value === '') {
          incompleteCount++;
          cellsToEnrich.add(`${columnName}:${row.id}`);
        }
      }

      if (incompleteCount === 0) {
        setIsEnriching(false);
        showToast('All enrichments are already complete for this column.', 'info');
        return;
      }

      // Set loading states for incomplete cells immediately
      setEnrichingCells(cellsToEnrich);
      setEnrichmentProgressData({
        column: columnName,
        total: incompleteCount,
        progress: 0,
        status: `Enriching ${columnName.slice(0, 10)}${columnName.length > 10 ? '...' : ''}`
      });

      // Use the new batch enrichment API - it will automatically handle incomplete cells
      try {
        onUpdatedDataset?.(tableId);
        await apiClient.enrichTableColumn(tableId, {
          column_name: columnName,
          row_limit: 'all'  // Enrich all rows, backend will handle incomplete logic
        });
      } catch (error: unknown) {
        console.error('Error enriching table column:', error);
        handleError(error as string | Error, 'Rerun Enrichment');
        // Clear loading states on error
        setEnrichingCells(new Set());
        throw error;
      }

      // Trigger data refresh to show loading states
      if (onDataRefresh) {
        onDataRefresh();
      }

      setIsEnriching(false);

    } catch (error: unknown) {
      console.error('Error during incomplete enrichment:', error);
      const err = error as { message?: string };
      showToast(`Error starting enrichment: ${err.message || 'Unknown error'}`, 'error');
      setIsEnriching(false);
      setEnrichingCells(new Set());
    }
  };

  // Handle delete column
  const handleDeleteColumn = async (columnName: string) => {
    try {
      await apiClient.deleteDatasetColumn(tableId, columnName);
      onUpdatedDataset?.(tableId);

      // Immediately update local columns state to remove the deleted column
      setColumns(prev => prev.filter(col => col.name !== columnName));

      // Refresh the table data to update lead data
      if (onDataRefresh) {
        onDataRefresh();
      }

      // Close modals
      setDeleteColumnModal({ show: false, columnName: '', columnKey: '' });
      setColumnEnrichmentPopup({ show: false, columnName: '', columnKey: '', x: 0, y: 0 });

    } catch (error: unknown) {
      console.error('Error deleting column:', error);
      showToast('Failed to delete column. Please try again.', 'error');
    }
  };

  const handleCellMouseDown = (e: React.MouseEvent, rowId: string, colId: string) => {
    if (e.button === 2) {
      return;
    }

    // Don't intercept clicks on links
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a')) {
      return;
    }

    // Hide hover preview when clicking on any cell
    closeHoverPreview();

    const isMultiSelect = e.metaKey || e.ctrlKey; // Cmd on Mac, Ctrl on Windows/Linux
    const isRangeSelect = e.shiftKey;

    // Row selection mode - handle row selection instead of cell selection
    if (rowSelectionMode) {
      e.preventDefault();
      setIsRowDragging(true);

      if (isRangeSelect && rowSelectionAnchor) {
        // Shift+Click: Select range of rows
        const anchorIdx = filteredLeads.findIndex(l => l.id === rowSelectionAnchor);
        const currentIdx = filteredLeads.findIndex(l => l.id === rowId);
        if (anchorIdx !== -1 && currentIdx !== -1) {
          const startIdx = Math.min(anchorIdx, currentIdx);
          const endIdx = Math.max(anchorIdx, currentIdx);
          const newSelection = new Set(effectiveSelectedRowIds);
          for (let i = startIdx; i <= endIdx; i++) {
            const id = filteredLeads[i]?.id;
            if (id) newSelection.add(id);
          }
          setEffectiveSelectedRowIds(newSelection);
        }
      } else if (isMultiSelect) {
        // Ctrl/Cmd+Click: Toggle row
        const newSelection = new Set(effectiveSelectedRowIds);
        if (newSelection.has(rowId)) {
          newSelection.delete(rowId);
        } else {
          newSelection.add(rowId);
        }
        setEffectiveSelectedRowIds(newSelection);
        setRowSelectionAnchor(rowId);
      } else {
        // Normal click: Start new selection
        setEffectiveSelectedRowIds(new Set([rowId]));
        setRowSelectionAnchor(rowId);
      }
      return;
    }

    // Normal cell selection mode - use hook's handler for drag state management
    hookCellMouseDown(e, rowId, colId);
  };

  const handleCellMouseEnter = (rowId: string, colId: string) => {
    // Row selection mode - handle row drag selection
    if (rowSelectionMode && isRowDragging && rowSelectionAnchor) {
      const anchorIdx = filteredLeads.findIndex(l => l.id === rowSelectionAnchor);
      const currentIdx = filteredLeads.findIndex(l => l.id === rowId);
      if (anchorIdx !== -1 && currentIdx !== -1) {
        const startIdx = Math.min(anchorIdx, currentIdx);
        const endIdx = Math.max(anchorIdx, currentIdx);
        const newSelection = new Set<string>();
        for (let i = startIdx; i <= endIdx; i++) {
          const id = filteredLeads[i]?.id;
          if (id) newSelection.add(id);
        }
        setEffectiveSelectedRowIds(newSelection);
      }
      return;
    }

    // Normal cell selection mode - use hook's handler for drag selection
    hookCellMouseEnter(rowId, colId);
  };

  const handleCellMouseUp = () => {
    // Use hook's handler to clear cell drag state
    hookCellMouseUp();
    // Also clear row selection drag state
    setIsRowDragging(false);
  };

  const handleEmailContextMenu = (e: React.MouseEvent, columnKey: string, email: string, lead: Lead) => {
    e.preventDefault();
    e.stopPropagation();
    logClick(e.currentTarget as HTMLElement, {
      action: 'email_context_menu',
      columnKey,
      leadId: lead.id,
      cellValue: email
    });
    
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const position = getPopupPosition(rect, 200, 80);
    
    setEmailContextMenu({
      show: true,
      email,
      leadData: lead,
      x: position.x,
      y: position.y
    });
  };

  const handleBulkEmailContextMenu = (e: React.MouseEvent, columnKey: string, lead: Lead, cellValue: string) => {
    e.preventDefault();
    e.stopPropagation();
    logClick(e.currentTarget as HTMLElement, {
      action: 'bulk_email_context_menu',
      columnKey,
      leadId: lead.id,
      cellValue
    });

    const existingSelection = new Set(Array.from(selection).map(key => {
      const parsedKey = parseCellKey(key);
      return parsedKey ? parsedKey.rowId : '';
    }));
    
    let newSelection = new Set(existingSelection);
    if (!existingSelection.has(lead.id)) {
      newSelection = new Set([lead.id]);
      setSelection(new Set());
      handleEmailContextMenu(e, columnKey, cellValue, lead);
      return;
    }
    setBulkEmailContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      selectedColumn: columnKey,
      selectedRows: newSelection
    });
  }

  const handleSendEmailClick = () => {
    if (emailContextMenu.email && emailContextMenu.leadData && onStartCampaignFlow) {
      const leadId = emailContextMenu.leadData.id;
      onStartCampaignFlow({
        email: emailContextMenu.email,
        leadData: emailContextMenu.leadData,
        selectedRowIds: leadId ? new Set([leadId]) : undefined
      });
      setEmailContextMenu({ show: false, email: '', leadData: {} as Lead, x: 0, y: 0 });
    }
  };

  // Click outside handler for popups
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Don't close popup if clicking inside any popup
      if (target.closest('.popup-container')) {
        return;
      }

      setColumnEnrichmentPopup(prev => ({ ...prev, show: false }));
      setEmailContextMenu(prev => ({ ...prev, show: false }));
    };

    if (columnEnrichmentPopup.show || emailContextMenu.show) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [columnEnrichmentPopup.show, emailContextMenu.show]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (bulkEmailContextMenu.show) {
        setBulkEmailContextMenu({ show: false, x: 0, y: 0, selectedColumn: '', selectedRows: new Set() });
      }
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setBulkEmailContextMenu({ show: false, x: 0, y: 0, selectedColumn: '', selectedRows: new Set() });
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [bulkEmailContextMenu.show]);


  // Close hover preview on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (hoverPreview.show) {
        closeHoverPreview();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [hoverPreview.show]);

  // Close hover preview on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && hoverPreview.show) {
        closeHoverPreview();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hoverPreview.show]);

  // Removed useMemo wrapper - with 40+ state dependencies, memoization provided no benefit
  // Component re-renders frequently anyway, and incomplete deps caused stale closures
  const tableContent = (
    <div className="h-full flex">
      {/* Global Animation Styles */}
      <style>{`
        @keyframes searchFadeIn {
          from { opacity: 0; transform: translateX(8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes searchFadeOut {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(8px); }
        }
      `}</style>

      {/* Main Table Container */}
      <div className="flex-1 flex flex-col bg-white transition-all duration-300">
        {/* Toolbar */}
        <TableToolbar
          columns={columns}
          tableType={tableType}
          leadsCount={leads.length}
          searchQuery={searchQuery}
          isSearchExpanded={isSearchExpanded}
          enrichmentProgressData={enrichmentProgressData}
          enrichingCellsCount={enrichingCells.size}
          onSearchChange={setSearchQuery}
          onSearchExpand={() => setIsSearchExpanded(true)}
          onSearchBlur={handleSearchBlur}
          onFilterChange={setFilterConditions}
          onSortChange={(sorts, fullMatches) => {
            setSortRules(sorts);
            setSelection(new Set<string>());
            setFullMatchesFirst(fullMatches);
          }}
        />

      {/* Table Container */}
      <div className="flex-1 relative bg-white min-h-0 overflow-hidden" ref={tableRef}>
        <div className="absolute inset-0 flex flex-col">
          <div className="flex-1 overflow-y-auto overflow-x-auto">
            <div className="min-h-full">
              <div
                className="bg-white"
                style={{
                  minWidth: columns.length > 0 ? `${columns.reduce((total, col) => total + col.width, 0) + COLUMN_WIDTHS.ROW_NUMBER + COLUMN_WIDTHS.ADD_COLUMN_SPACER}px` : '100%'
                }}
              >
          {/* Progress Bar - Above column headers */}
          {progressData && progressData.status === 'in_progress' && (
            <LeadGenProgressBar progressData={progressData} tableType={tableType} />
          )}

          {/* Table Header Row - only show if we have columns */}
          {columns.length > 0 && (
          <div className="sticky top-0 bg-neutral-50/80 backdrop-blur-sm border-b z-30" style={{ borderBottomColor: colors.neutral[200] }} data-tour="criteria-headers">
            <div
              className="flex"
              style={{
                width: `${columns.reduce((total, col) => total + col.width, 0) + COLUMN_WIDTHS.ROW_NUMBER + COLUMN_WIDTHS.ADD_COLUMN_SPACER}px`
              }}
            >
              {/* Row number header cell - select all */}
              <SelectAllHeaderCell
                rowSelectionMode={rowSelectionMode}
                allRowsSelected={(() => {
                  const allRowIds = filteredLeads.map(l => l.id).filter(Boolean) as string[];
                  return rowSelectionMode && allRowIds.length > 0 && allRowIds.every(id => effectiveSelectedRowIds.has(id));
                })()}
                someRowsSelected={rowSelectionMode && effectiveSelectedRowIds.size > 0}
                selectionSize={selection.size}
                onClick={handleSelectAll}
              />
              {columns.map((column, index) => {
                // Check if any cell in this column is selected
                const columnHasSelection = filteredLeads.some(lead =>
                  lead.id && selection.has(makeCellKey(lead.id, column.key))
                );

                return (
                  <TableHeaderCell
                    key={column.key}
                    column={column}
                    columnIndex={index}
                    tableType={tableType}
                    columnHasSelection={columnHasSelection}
                    draggingColumnIndex={draggingColumnIndex}
                    dragOverColumnIndex={dragOverColumnIndex}
                    editingColumn={editingColumn}
                    editColumnName={editColumnName}
                    sortDirection={getColumnSortDirection(column.name)}
                    onDragStart={handleColumnDragStart}
                    onDragEnd={handleColumnDragEnd}
                    onDragOver={handleColumnDragOver}
                    onDragEnter={handleColumnDragEnter}
                    onDragLeave={handleColumnDragLeave}
                    onDrop={handleColumnDrop}
                    onResizeStart={handleMouseDown}
                    onSort={handleColumnSort}
                    onStartEdit={startColumnEdit}
                    onEditChange={setEditColumnName}
                    onEditComplete={handleColumnRename}
                    onEditCancel={() => {
                      setEditingColumn(null);
                      setEditColumnName('');
                    }}
                    onOpenEnrichmentMenu={handleOpenEnrichmentMenu}
                    onSelectColumn={handleSelectColumn}
                    onHoverPreviewEnter={handleHeaderHoverPreview}
                    onHoverPreviewLeave={handleHeaderHoverPreviewLeave}
                  />
                );
              })}
              {/* Add Column Button */}
              <AddColumnButton
                onClick={() => {
                  if (isPublicView) {
                    window.open(getAppUrl(), '_blank');
                    return;
                  }
                  // Prefer chat-based enrichment flow if available
                  if (onStartEnrichmentFlow) {
                    onStartEnrichmentFlow();
                  } else {
                    setShowAddColumn(true);
                  }
                }}
              />
            </div>
          </div>
          )}

          {/* Table Body */}
          <div className="bg-gray-50/30 relative">
            {isLoadingRows ? (
              // Show skeleton with table type appropriate columns
              // Columns are always set during loading based on table type
              columns.length > 0 ? (
                <RowLoadingSkeleton rows={12} columns={columns} />
              ) : (
                <div className="flex items-center justify-center bg-white min-h-[400px]">
                  <Spinner size="lg" text="Loading table..." />
                </div>
              )
            ) : filteredLeads.length === 0 ? (
              <EmptyLeadState
                tableStatus={tableStatus || ''}
                errorMessage={errorMessage}
                prospectsAnalyzed={prospectsAnalyzed}
              />
            ) : (
              filteredLeads.map((lead, rowIndex) => {
                // Check if any cell in this row is selected or is the anchor
                const rowHasCellSelection = lead.id && columns.some(col =>
                  isCellSelected(lead.id!, col.key) || isAnchorCell(lead.id!, col.key)
                );
                // Check if row is selected in row selection mode
                const rowIsSelected = !!(rowSelectionMode && lead.id && effectiveSelectedRowIds.has(lead.id));
                // Check if row is visually selected (from campaign flow feedback)
                const rowIsVisuallySelected = visualSelectionMode === 'all' ||
                  (visualSelectionMode === 'firstN' && rowIndex < visualSelectionCount);
                const rowHasSelection = rowHasCellSelection || rowIsSelected || rowIsVisuallySelected;

                const isInterim = (lead as any).is_interim;

                return (
                  <div
                    key={lead.id || rowIndex}
                    className={`flex border-b group ${
                      isInterim ? 'opacity-75' : ''
                    }`}
                    style={{
                      width: `${columns.reduce((total, col) => total + col.width, 0) + 40 + 48}px`, // Row number (40) + column widths + spacer
                      borderBottomColor: colors.neutral[100],
                      backgroundColor: isInterim
                        ? '#f8fafc' // Very subtle blue tint for interim results
                        : rowIsVisuallySelected
                          ? colors.primary[50] // Light highlight for visual selection feedback
                          : rowHasSelection
                            ? colors.primary[50] // Light blue for selected rows
                            : 'white',
                      transition: 'background-color 120ms ease-out'
                    }}
                  >
                    <RowNumberCell
                      rowIndex={rowIndex}
                      rowId={lead.id}
                      rowSelectionMode={rowSelectionMode}
                      isRowSelected={rowIsSelected}
                      rowHasSelection={rowHasSelection}
                      isVisuallySelected={rowIsVisuallySelected}
                      isInterim={isInterim}
                      onMouseDown={(e) => {
                        if (!lead.id) return;
                        if (rowSelectionMode) {
                          setIsRowDragging(true);
                          e.preventDefault();
                        }
                      }}
                      onMouseEnter={() => {
                        if (rowSelectionMode && isRowDragging && lead.id && rowSelectionAnchor) {
                          const anchorIdx = filteredLeads.findIndex(l => l.id === rowSelectionAnchor);
                          const currentIdx = rowIndex;
                          if (anchorIdx !== -1) {
                            const startIdx = Math.min(anchorIdx, currentIdx);
                            const endIdx = Math.max(anchorIdx, currentIdx);
                            const newSelection = new Set<string>();
                            for (let i = startIdx; i <= endIdx; i++) {
                              const id = filteredLeads[i]?.id;
                              if (id) newSelection.add(id);
                            }
                            setEffectiveSelectedRowIds(newSelection);
                          }
                        }
                      }}
                      onMouseUp={() => setIsRowDragging(false)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!lead.id) return;

                        if (rowSelectionMode) {
                          const isCtrlOrCmd = e.ctrlKey || e.metaKey;
                          const isShift = e.shiftKey;

                          if (isShift && rowSelectionAnchor) {
                            const anchorIdx = filteredLeads.findIndex(l => l.id === rowSelectionAnchor);
                            const currentIdx = rowIndex;
                            if (anchorIdx !== -1) {
                              const startIdx = Math.min(anchorIdx, currentIdx);
                              const endIdx = Math.max(anchorIdx, currentIdx);
                              const newSelection = new Set(effectiveSelectedRowIds);
                              for (let i = startIdx; i <= endIdx; i++) {
                                const id = filteredLeads[i]?.id;
                                if (id) newSelection.add(id);
                              }
                              setEffectiveSelectedRowIds(newSelection);
                            }
                          } else if (isCtrlOrCmd) {
                            const newSelection = new Set(effectiveSelectedRowIds);
                            if (newSelection.has(lead.id)) {
                              newSelection.delete(lead.id);
                            } else {
                              newSelection.add(lead.id);
                            }
                            setEffectiveSelectedRowIds(newSelection);
                            setRowSelectionAnchor(lead.id);
                          } else {
                            setEffectiveSelectedRowIds(new Set([lead.id]));
                            setRowSelectionAnchor(lead.id);
                          }
                        } else {
                          const rowCells = new Set<string>();
                          columns.forEach(col => {
                            if (col.key) {
                              rowCells.add(makeCellKey(lead.id!, col.key));
                            }
                          });
                          setSelection(rowCells);
                          setAnchor({ rowId: lead.id, colId: columns[0]?.key || '' });
                          if (onRowClick) {
                            onRowClick(lead.id);
                          }
                        }
                      }}
                    />
                    {columns.map((column, colIndex) => {
                      if (!lead.id) return null;

                      const cellValue = getCellValue(lead, column.key, column.name);
                      const isUrlValue = isURL(cellValue);
                      const isValidEmail = isEmail(cellValue);
                      const isNameColumn = column.key === 'name' || column.name === 'Name';
                      const displayUrl = getDisplayUrl(lead);
                      const hasDisplayUrl = isNameColumn && displayUrl;
                      const isUnknown = cellValue === 'unknown';
                      const isSegmentColumn = column.isSegment ?? false;
                      const isMonitoredColumn = column.key === 'monitored_time';
                      const isMatch = cellValue === 'Match';
                      const isMiss = cellValue === 'Miss';
                      const isLoading = isCellLoading(lead, column.name) || enrichingCells.has(`${column.name}:${lead.id}`);

                      // Check for enrichment data
                      const cached = enrichmentCache.current.get(lead.id);
                      const hasEnrichmentData = !!(cached && cached[column.name] &&
                        (cached[column.name].summary || cached[column.name].dataSources?.length > 0));

                      // Check for reasoning data
                      const hasReasoning = !!(lead.reasoning && Object.keys(lead.reasoning).length > 0);

                      // Check if this is the first segment cell in the first row (for tour)
                      const isFirstSegmentCell = rowIndex === 0 && isSegmentColumn &&
                        columns.filter(c => c.isSegment).findIndex(c => c.key === column.key) === 0;

                      return (
                        <TableCell
                          key={column.key}
                          lead={lead}
                          column={column}
                          columnIndex={colIndex}
                          cellValue={cellValue}
                          isLoading={isLoading}
                          isUrl={isUrlValue}
                          isEmail={isValidEmail}
                          isNameColumn={isNameColumn}
                          displayUrl={displayUrl}
                          isUnknown={isUnknown}
                          isSegmentColumn={isSegmentColumn}
                          isMonitoredColumn={isMonitoredColumn}
                          isMatch={isMatch}
                          isMiss={isMiss}
                          isInterim={isInterim}
                          rowHasSelection={rowHasSelection}
                          hasEnrichmentData={hasEnrichmentData}
                          hasReasoning={hasReasoning}
                          dataTour={isFirstSegmentCell ? 'match-cell' : undefined}
                          onMouseDown={(e) => handleCellMouseDown(e, lead.id!, column.key)}
                          onMouseEnter={() => handleCellMouseEnter(lead.id!, column.key)}
                          onMouseUp={handleCellMouseUp}
                          onClick={(e) => {
                            e.stopPropagation();
                            logClick(e.currentTarget as HTMLElement, {
                              action: 'cell_click',
                              columnKey: column.key,
                              leadId: lead.id,
                              cellValue
                            });

                            // Check if cell is selected and contains URL
                            const cellIsCurrentlySelected = isCellSelected(lead.id!, column.key);
                            if (cellIsCurrentlySelected && (isUrlValue || hasDisplayUrl)) {
                              window.open(hasDisplayUrl ? displayUrl : cellValue, '_blank', 'noopener,noreferrer');
                              return;
                            }

                            // Segment column - open MatchDetailsPopover
                            if (isSegmentColumn && hasReasoning) {
                              const companyName = lead.name || lead.data?.name || lead.data?.Name || 'Company';
                              const companyUrl = lead.data?.url || lead.data?.URL || lead.data?.website;
                              let status: 'match' | 'no-match' | 'partial' = 'match';
                              if (cellValue === 'Miss') status = 'no-match';
                              else if (cellValue === 'Unclear') status = 'partial';
                              const reasoningEntries = Object.entries(lead.reasoning!);
                              const criterionEntry = reasoningEntries.find(([, r]) =>
                                r.criterion_text === column.name ||
                                column.name.toLowerCase().includes(r.criterion_text?.toLowerCase()?.substring(0, 20))
                              );
                              const criterionIndex = criterionEntry
                                ? reasoningEntries.findIndex(([k]) => k === criterionEntry[0]) + 1
                                : 1;
                              setMatchPopover({
                                isOpen: true,
                                anchorRect: e.currentTarget.getBoundingClientRect(),
                                companyName,
                                companyUrl,
                                criterion: criterionEntry ? criterionEntry[1] : reasoningEntries[0]?.[1] || null,
                                criterionIndex,
                                overallStatus: status,
                                reasoning: lead.reasoning!
                              });
                            }

                            // Base column - open CellContentPopover
                            const isBase = isBaseColumn(column.name, tableType || 'companies');
                            if (isBase && cellValue && cellValue.trim()) {
                              closeHoverPreview();
                              setCellContentPopover({
                                isOpen: true,
                                anchorRect: e.currentTarget.getBoundingClientRect(),
                                content: cellValue,
                                columnName: column.name,
                                isEmail: isValidEmail,
                                isUrl: Boolean(isUrlValue || hasDisplayUrl),
                                companyName: lead.name || lead.data?.name || lead.data?.Name || '',
                                leadData: lead
                              });
                            }
                            // Non-segment, non-base column - open EnrichmentDetailsPopover
                            else if (!isSegmentColumn && !isBase && !column.name.toLowerCase().includes('confidence')) {
                              openEnrichmentPopover(lead, column.name, e.currentTarget.getBoundingClientRect());
                            }
                          }}
                          onContextMenu={isValidEmail ? (e) => {
                            if (isPublicView) {
                              e.preventDefault();
                              window.open(getAppUrl(), '_blank');
                              return;
                            }
                            closeHoverPreview();
                            if (selection.size > 1) {
                              handleBulkEmailContextMenu(e, column.name, lead, cellValue);
                            } else if (lead.id) {
                              handleEmailContextMenu(e, column.name, cellValue, lead);
                            }
                          } : undefined}
                          onExpandEnrichment={(e) => openEnrichmentPopover(lead, column.name, e.currentTarget.getBoundingClientRect())}
                          onExpandMatch={(e) => {
                            e.stopPropagation();
                            const cellElement = e.currentTarget.parentElement;
                            if (cellElement) {
                              openMatchPopover(lead, column.name, cellElement);
                            }
                          }}
                        />
                      );
                    })}
                    {/* Spacer for add column button */}
                      <div
                        className="flex-shrink-0 w-12 min-w-12 max-w-12 box-border border-r border-solid border-gray-200 bg-white"
                        style={{
                          borderRightColor: colors.neutral[200]
                        }}
                      />
                  </div>
                );
              })
            )}

            {/* Selection Marquee Overlay (Excel/Sheets style) */}
            <SelectionMarquee
              selection={selection}
              leads={filteredLeads}
              columns={columns}
              computeSelectionRectangles={computeSelectionRectangles}
              containerRef={tableRef}
            />
              </div>
            </div>
          </div>

        </div>
      </div>

        {/* Column Enrichment Popup */}
        <ColumnEnrichmentMenu
          menuState={columnEnrichmentPopup}
          isPublicView={isPublicView}
          isEnriching={isEnriching}
          tableType={tableType}
          onRerunEnrichments={handleRerunIncompleteEnrichments}
          onBulkEmailClick={(columnName) => handleBulkEmailClick(columnName, filteredLeads)}
          onDeleteClick={() => {
            setDeleteColumnModal({
              show: true,
              columnName: columnEnrichmentPopup.columnName,
              columnKey: columnEnrichmentPopup.columnKey
            });
          }}
          onClose={() => setColumnEnrichmentPopup({ show: false, columnName: '', columnKey: '', x: 0, y: 0 })}
          logClick={logClick}
        />

        {/* Email Context Menu */}
        <EmailContextMenu
          menuState={emailContextMenu}
          isPublicView={isPublicView}
          onSendEmail={handleSendEmailClick}
        />

        {/* Bulk Email Context Menu */}
        <BulkEmailContextMenu
          menuState={bulkEmailContextMenu}
          onSendCampaign={contextBulkEmailClick}
        />

      </div>
    </div>

      {/* Delete Column Confirmation Modal */}
      <DeleteColumnModal
        modalState={deleteColumnModal}
        onConfirm={(columnName) => handleDeleteColumn(columnName)}
        onClose={() => setDeleteColumnModal({ show: false, columnName: '', columnKey: '' })}
      />

      {/* Add Column Modal */}
      <AddColumnModal
        isOpen={showAddColumn}
        isEnriching={isEnriching}
        onClose={handleCloseModal}
        onAddColumn={async (columnName, formatHint) => {
          if (!tableId || isEnriching) return;

          try {
            onUpdatedDataset?.(tableId);
            setIsEnriching(true);

            // Create column
            const result = await apiClient.ensureTableColumn(tableId, columnName, 'text');
            if (!result.success) {
              throw new Error(result.message || 'Failed to create column');
            }

            // Update local columns state
            const newColumn = {
              key: columnName.toLowerCase().replace(/\s+/g, '_'),
              name: columnName,
              width: 150
            };
            setColumns(prev => {
              if (prev.find(col => col.name === columnName)) return prev;
              return [...prev, newColumn];
            });

            // Call parent callback
            if (onAddColumn) {
              onAddColumn(newColumn.name, 'text');
            }

            // Set loading state on cells
            const cellsToEnrich = new Set<string>();
            leads.forEach(lead => {
              if (lead.id) cellsToEnrich.add(`${columnName}:${lead.id}`);
            });
            setEnrichingCells(cellsToEnrich);
            setEnrichmentProgressData({
              column: columnName,
              total: leads.length,
              progress: 0,
              status: `Enriching ${columnName.slice(0, 10)}${columnName.length > 10 ? '...' : ''}`
            });

            // Start enrichment
            await apiClient.enrichTableColumn(tableId, {
              column_name: columnName,
              row_limit: 'all',
              format_hint: formatHint || undefined
            });

            showToast(`Column '${columnName}' added. AI enrichment started on ${leads.length} visible rows.`, 'success');
            setIsEnriching(false);
          } catch (error) {
            console.error('Error adding column:', error);
            handleError(error as string | Error, 'Column Enrichment');
            setIsEnriching(false);
            setEnrichingCells(new Set());
            setEnrichmentProgressData(null);
          }
        }}
      />

      {/* Match Details Popover (cell-anchored) */}
      <MatchDetailsPopover
        isOpen={matchPopover.isOpen}
        onClose={() => setMatchPopover({
          isOpen: false,
          anchorRect: null,
          companyName: '',
          criterion: null,
          criterionIndex: 1,
          overallStatus: 'match',
          reasoning: {}
        })}
        anchorRect={matchPopover.anchorRect}
        companyName={matchPopover.companyName}
        companyUrl={matchPopover.companyUrl}
        criterion={matchPopover.criterion}
        criterionIndex={matchPopover.criterionIndex}
        overallStatus={matchPopover.overallStatus}
        totalCriteria={Object.keys(matchPopover.reasoning).length}
        reasoning={matchPopover.reasoning}
        onNavigate={(newIndex) => {
          // Navigate to different criterion
          const reasoningEntries = Object.entries(matchPopover.reasoning);
          if (reasoningEntries[newIndex]) {
            const [, criterion] = reasoningEntries[newIndex];
            const criterionStatus = (criterion as any).satisfied === 'yes' ? 'match' : (criterion as any).satisfied === 'no' ? 'no-match' : 'partial';
            setMatchPopover(prev => ({
              ...prev,
              criterion: criterion as any,
              criterionIndex: newIndex + 1,
              overallStatus: criterionStatus
            }));
          }
        }}
        onViewAllCriteria={() => {
          // Open full modal with all criteria
          setCompanyReasoningModal({
            isOpen: true,
            companyName: matchPopover.companyName,
            companyUrl: matchPopover.companyUrl,
            summary: undefined,
            reasoning: matchPopover.reasoning,
            overallStatus: matchPopover.overallStatus,
            criteria: undefined  // Show all criteria
          });
        }}
      />

      {/* Cell Content Popover (for Name, Description, URL cells) */}
      <CellContentPopover
        isOpen={cellContentPopover.isOpen}
        onClose={() => setCellContentPopover({
          isOpen: false,
          anchorRect: null,
          content: '',
          columnName: '',
          isEmail: false,
          isUrl: false,
          companyName: '',
          leadData: null
        })}
        anchorRect={cellContentPopover.anchorRect}
        content={cellContentPopover.content}
        columnName={cellContentPopover.columnName}
        isEmail={cellContentPopover.isEmail}
        isUrl={cellContentPopover.isUrl}
        companyName={cellContentPopover.companyName}
        isPublicView={isPublicView}
        onSendEmail={() => {
          if (cellContentPopover.leadData && cellContentPopover.isEmail && onStartCampaignFlow) {
            const leadId = cellContentPopover.leadData.id;
            onStartCampaignFlow({
              email: cellContentPopover.content,
              leadData: cellContentPopover.leadData,
              selectedRowIds: leadId ? new Set([leadId]) : undefined
            });
            setCellContentPopover(prev => ({ ...prev, isOpen: false }));
          }
        }}
      />

      {/* Company Reasoning Modal (full view for all criteria) */}
      <CompanyReasoningModal
        isOpen={companyReasoningModal.isOpen}
        onClose={() => setCompanyReasoningModal({
          isOpen: false,
          companyName: '',
          reasoning: {},
          overallStatus: 'match',
          criteria: undefined
        })}
        companyName={companyReasoningModal.companyName}
        companyUrl={companyReasoningModal.companyUrl}
        summary={companyReasoningModal.summary}
        reasoning={companyReasoningModal.reasoning}
        overallStatus={companyReasoningModal.overallStatus}
        criteria={companyReasoningModal.criteria}
      />

      {/* Enrichment Details Popover (cell-anchored) */}
      <EnrichmentDetailsPopover
        isOpen={enrichmentPopover.isOpen}
        onClose={() => setEnrichmentPopover({
          isOpen: false,
          anchorRect: null,
          columnName: '',
          rowName: '',
          cellValue: '',
          enrichmentSummary: '',
          dataSources: [],
          isLoading: false,
          leadData: null
        })}
        anchorRect={enrichmentPopover.anchorRect}
        columnName={enrichmentPopover.columnName}
        rowName={enrichmentPopover.rowName}
        cellValue={enrichmentPopover.cellValue}
        enrichmentSummary={enrichmentPopover.enrichmentSummary}
        dataSources={enrichmentPopover.dataSources}
        isLoading={enrichmentPopover.isLoading}
        onStartCampaign={onStartCampaignFlow && enrichmentPopover.leadData ? () => {
          const leadId = enrichmentPopover.leadData?.id;
          onStartCampaignFlow({
            leadData: enrichmentPopover.leadData!,
            selectedRowIds: leadId ? new Set([leadId]) : undefined
          });
        } : undefined}
      />

      {/* Hover Preview Tooltip */}
      <HoverPreviewTooltip
        hoverPreview={hoverPreview}
        hoverPreviewRef={hoverPreviewRef}
        isMouseInPreview={isMouseInPreview}
        onClose={closeHoverPreview}
      />

      {/* Out of Credits Modal */}
      <OutOfCreditsModal isOpen={showOutOfCreditsModal} onClose={closeOutOfCreditsModal} />

    </div>
  );

  // Mobile view for responsive design
  const mobileContent = (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <MobileDatasetView
        leads={filteredLeads}
        segments={segments}
        tableName={tableName}
        isLoading={isLoadingInitialData}
        onShare={onShare}
        onExportCSV={onExportCSV}
        onExportExcel={onExportExcel}
        onDelete={onDelete}
      />
    </div>
  );

  // Choose between mobile and desktop view
  const content = isMobile ? mobileContent : tableContent;

  return content;
});

ProfessionalTable.displayName = 'ProfessionalTable';